// ─────────────────────────────────────────────────────────────────────────────
// debug_dialog.ts — Generic tabbed debug dialog
//
// A themed, draggable panel that surfaces every page-scoped lever in the
// app — page styling, registered spells, manual invocations, utility
// helpers — through a tab + subtab + action-row structure.
//
// This file owns only the framework (layout, rendering, hit-testing,
// dispatch into the active action's `onClick`).  The content (which
// tabs exist, what they contain) is supplied by the caller via the
// `tabs` accessor — typically rebuilt every frame so each row's
// `selected` / `detail` reflects current state without a callback web.
//
// Visual style mirrors the in-app pop-dialogs: parchment fill, sepia
// hairline border, drag handle top-left, close X top-right. The
// frame doesn't try to compete with the hand-drawn PopDialogLayer —
// this is a developer tool, structure beats artistry.
// ─────────────────────────────────────────────────────────────────────────────

export interface DebugAction {
  /** Unique within its (sub)tab. */
  id: string;
  /** Row label. */
  label: string;
  /** Optional small detail / hint shown right-aligned. */
  detail?: string;
  /** Radio-style highlight (e.g. "this style is currently applied"). */
  selected?: boolean;
  /** Fired on tap. */
  onClick(): void;
}

export interface DebugSubtab {
  id: string;
  label: string;
  actions: DebugAction[];
}

export interface DebugTab {
  id: string;
  label: string;
  /** Either subtabs OR flat actions (not both). */
  subtabs?: DebugSubtab[];
  actions?: DebugAction[];
}

/** Caller-supplied tabs source. Re-evaluated every frame so rows can
 *  reflect live state (e.g. current page's active decoration). */
export type TabsSource = () => DebugTab[];

// ── Layout constants ────────────────────────────────────────────────────────

const PANEL_W       = 320;
const PANEL_PAD_X   = 12;
const PANEL_PAD_Y   = 10;
const TITLE_H       = 22;
const TAB_H         = 24;
const SUBTAB_H      = 22;
const ROW_H         = 24;
const MAX_ROWS      = 14;
const CLOSE_R       = 8;
const DRAG_R        = 8;

interface RowHit {
  kind: 'tab'      | 'subtab'      | 'row'    | 'close' | 'drag' | 'panel' | 'scrollUp' | 'scrollDown';
  index?: number;        // tab/subtab/row index
}

interface FrameLayout {
  bg: { x: number; y: number; w: number; h: number };
  closeBtn: { x: number; y: number; r: number };
  dragBtn: { x: number; y: number; r: number };
  tabRects: Array<{ x: number; y: number; w: number; h: number }>;
  subtabRects: Array<{ x: number; y: number; w: number; h: number }>;
  rowRects: Array<{ x: number; y: number; w: number; h: number }>;
  scrollUp?: { x: number; y: number; r: number };
  scrollDown?: { x: number; y: number; r: number };
  /** First visible row index (for scroll). */
  firstRowIdx: number;
  /** Total rows after scroll trimming (visible only). */
  visibleRows: DebugAction[];
}

// ── Dialog ──────────────────────────────────────────────────────────────────

export class DebugDialog {
  /** Anchor position (CSS-px, canvas-absolute). When non-null, the dialog
   *  is open and laid out around this point. Drag updates the anchor. */
  private _anchor: { x: number; y: number } | null = null;
  /** Selected tab + subtab ids; null = pick the first available. */
  private _activeTab = '';
  private _activeSubtab = '';
  /** Vertical scroll offset for the row list. */
  private _scrollOffset = 0;
  /** Drag state. */
  private _drag: { dx: number; dy: number } | null = null;
  /** Last layout computed by draw(); reused for hit-testing. */
  private _layout: FrameLayout | null = null;
  /** Last viewport seen, for re-clamping after drag. */
  private _viewport = { w: 0, h: 0 };

  constructor(private readonly _tabsSource: TabsSource) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  open(cx: number, cy: number): void {
    this._anchor = { x: cx, y: cy };
    this._scrollOffset = 0;
    // Default to first tab / first subtab on open.
    const tabs = this._tabsSource();
    if (tabs.length > 0) {
      if (!tabs.find(t => t.id === this._activeTab)) {
        this._activeTab = tabs[0].id;
      }
      const active = tabs.find(t => t.id === this._activeTab);
      if (active?.subtabs && active.subtabs.length > 0
          && !active.subtabs.find(s => s.id === this._activeSubtab)) {
        this._activeSubtab = active.subtabs[0].id;
      }
    }
  }

  dismiss(): void {
    this._anchor = null;
    this._drag = null;
    this._layout = null;
  }

  toggle(cx: number, cy: number): void {
    if (this.isOpen()) this.dismiss();
    else this.open(cx, cy);
  }

  isOpen(): boolean { return this._anchor !== null; }

  // ── Hit testing ───────────────────────────────────────────────────────────

  /** Returns where (x, y) landed on the panel — null if outside.
   *  Caller should call `applyHit` to dispatch the effect. */
  hitTest(x: number, y: number): RowHit | null {
    const L = this._layout;
    if (!L) return null;
    // Close
    if (dist(x, y, L.closeBtn.x, L.closeBtn.y) <= L.closeBtn.r + 6) {
      return { kind: 'close' };
    }
    // Drag
    if (dist(x, y, L.dragBtn.x, L.dragBtn.y) <= L.dragBtn.r + 6) {
      return { kind: 'drag' };
    }
    // Tabs
    for (let i = 0; i < L.tabRects.length; i++) {
      const r = L.tabRects[i];
      if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) {
        return { kind: 'tab', index: i };
      }
    }
    // Subtabs
    for (let i = 0; i < L.subtabRects.length; i++) {
      const r = L.subtabRects[i];
      if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) {
        return { kind: 'subtab', index: i };
      }
    }
    // Scroll arrows
    if (L.scrollUp && dist(x, y, L.scrollUp.x, L.scrollUp.y) <= L.scrollUp.r + 6) {
      return { kind: 'scrollUp' };
    }
    if (L.scrollDown && dist(x, y, L.scrollDown.x, L.scrollDown.y) <= L.scrollDown.r + 6) {
      return { kind: 'scrollDown' };
    }
    // Rows
    for (let i = 0; i < L.rowRects.length; i++) {
      const r = L.rowRects[i];
      if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) {
        return { kind: 'row', index: i };
      }
    }
    // Panel chrome (consume tap so it doesn't fall through)
    if (x >= L.bg.x && x <= L.bg.x + L.bg.w &&
        y >= L.bg.y && y <= L.bg.y + L.bg.h) {
      return { kind: 'panel' };
    }
    return null;
  }

  /** Apply a hit. Returns `'consumed'` when the tap is handled internally
   *  (no further pointer-pipeline processing should happen), `'dismiss'`
   *  when the dialog wants to be torn down by the caller (outside tap),
   *  or `'drag-begin'` when the tap starts a drag. */
  applyHit(hit: RowHit | null, hitX: number, hitY: number): 'consumed' | 'dismiss' | 'drag-begin' {
    if (!hit) return 'dismiss';
    switch (hit.kind) {
      case 'close':
        this.dismiss();
        return 'consumed';
      case 'drag': {
        if (!this._anchor) return 'consumed';
        this._drag = {
          dx: hitX - this._anchor.x,
          dy: hitY - this._anchor.y,
        };
        return 'drag-begin';
      }
      case 'tab': {
        const tabs = this._tabsSource();
        const t = tabs[hit.index ?? -1];
        if (t) {
          this._activeTab = t.id;
          if (t.subtabs && t.subtabs.length > 0) {
            this._activeSubtab = t.subtabs[0].id;
          } else {
            this._activeSubtab = '';
          }
          this._scrollOffset = 0;
        }
        return 'consumed';
      }
      case 'subtab': {
        const tabs = this._tabsSource();
        const t = tabs.find(x => x.id === this._activeTab);
        const s = t?.subtabs?.[hit.index ?? -1];
        if (s) {
          this._activeSubtab = s.id;
          this._scrollOffset = 0;
        }
        return 'consumed';
      }
      case 'scrollUp':
        this._scrollOffset = Math.max(0, this._scrollOffset - 3);
        return 'consumed';
      case 'scrollDown':
        this._scrollOffset += 3;
        // Clamp on next draw via layout.
        return 'consumed';
      case 'row': {
        const L = this._layout;
        const idx = hit.index ?? -1;
        const action = L?.visibleRows[idx];
        if (action) {
          try { action.onClick(); }
          catch (err) { console.error('[debug-dialog] action threw', err); }
        }
        return 'consumed';
      }
      case 'panel':
        return 'consumed';
    }
  }

  /** Drag in-progress — update the anchor position from a pointer move. */
  drag(x: number, y: number): void {
    if (!this._drag || !this._anchor) return;
    this._anchor.x = x - this._drag.dx;
    this._anchor.y = y - this._drag.dy;
    // Clamp on next draw against the latest viewport.
  }

  endDrag(): void { this._drag = null; }

  // ── Render ────────────────────────────────────────────────────────────────

  draw(ctx: CanvasRenderingContext2D, viewport: { w: number; h: number }): void {
    if (!this._anchor) { this._layout = null; return; }
    this._viewport = viewport;
    const tabs = this._tabsSource();
    const L = this._computeLayout(tabs, viewport);
    this._layout = L;

    ctx.save();
    // Soft shadow
    ctx.shadowColor = 'rgba(20, 14, 8, 0.35)';
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 2;
    // Backplate
    ctx.fillStyle = '#f3e6c7';
    ctx.strokeStyle = '#3a2010';
    ctx.lineWidth = 0.8;
    roundRect(ctx, L.bg.x, L.bg.y, L.bg.w, L.bg.h, 6);
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
    ctx.stroke();

    // Drag handle (3 short lines)
    ctx.strokeStyle = 'rgba(58, 32, 16, 0.55)';
    ctx.lineWidth = 1;
    const dh = L.dragBtn;
    ctx.beginPath();
    for (let i = -3; i <= 3; i += 3) {
      ctx.moveTo(dh.x - 5, dh.y + i);
      ctx.lineTo(dh.x + 5, dh.y + i);
    }
    ctx.stroke();

    // Close X
    const cb = L.closeBtn;
    ctx.beginPath();
    ctx.moveTo(cb.x - 4, cb.y - 4); ctx.lineTo(cb.x + 4, cb.y + 4);
    ctx.moveTo(cb.x + 4, cb.y - 4); ctx.lineTo(cb.x - 4, cb.y + 4);
    ctx.stroke();

    // Title text
    ctx.fillStyle = '#3a2010';
    ctx.font = 'bold 10px Georgia, serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('DEBUG', L.bg.x + L.bg.w / 2, L.bg.y + TITLE_H / 2);

    // Tabs
    ctx.font = 'bold 10px Georgia, serif';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    for (let i = 0; i < tabs.length; i++) {
      const r = L.tabRects[i];
      const isActive = tabs[i].id === this._activeTab;
      // Underline for active
      if (isActive) {
        ctx.fillStyle = 'rgba(58, 32, 16, 0.10)';
        roundRect(ctx, r.x + 2, r.y + 2, r.w - 4, r.h - 4, 3);
        ctx.fill();
      }
      ctx.fillStyle = isActive ? '#3a2010' : 'rgba(58, 32, 16, 0.6)';
      ctx.fillText(tabs[i].label, r.x + r.w / 2, r.y + r.h / 2);
    }
    // Tab row underline (separator)
    ctx.strokeStyle = 'rgba(58, 32, 16, 0.25)';
    ctx.lineWidth = 0.6;
    ctx.beginPath();
    ctx.moveTo(L.bg.x + 8, L.bg.y + TITLE_H + TAB_H);
    ctx.lineTo(L.bg.x + L.bg.w - 8, L.bg.y + TITLE_H + TAB_H);
    ctx.stroke();

    // Subtabs (if any)
    if (L.subtabRects.length > 0) {
      ctx.font = 'italic 9.5px Georgia, serif';
      const active = tabs.find(t => t.id === this._activeTab);
      const subs = active?.subtabs ?? [];
      for (let i = 0; i < L.subtabRects.length; i++) {
        const r = L.subtabRects[i];
        const isActive = subs[i].id === this._activeSubtab;
        if (isActive) {
          ctx.fillStyle = 'rgba(58, 32, 16, 0.16)';
          roundRect(ctx, r.x + 1, r.y + 2, r.w - 2, r.h - 4, 3);
          ctx.fill();
        }
        ctx.fillStyle = isActive ? '#3a2010' : 'rgba(58, 32, 16, 0.55)';
        ctx.fillText(subs[i].label, r.x + r.w / 2, r.y + r.h / 2);
      }
    }

    // Rows
    ctx.font = '11px Georgia, serif';
    ctx.textBaseline = 'middle';
    for (let i = 0; i < L.rowRects.length; i++) {
      const r = L.rowRects[i];
      const action = L.visibleRows[i];
      if (action.selected) {
        ctx.fillStyle = 'rgba(58, 32, 16, 0.18)';
        roundRect(ctx, r.x, r.y, r.w, r.h, 3);
        ctx.fill();
      }
      ctx.fillStyle = '#3a2010';
      ctx.textAlign = 'left';
      ctx.fillText(action.label, r.x + 8, r.y + r.h / 2);
      if (action.detail) {
        ctx.fillStyle = 'rgba(58, 32, 16, 0.55)';
        ctx.textAlign = 'right';
        ctx.fillText(action.detail, r.x + r.w - 8, r.y + r.h / 2);
      }
    }

    // Scroll arrows
    if (L.scrollUp) {
      drawTriangle(ctx, L.scrollUp.x, L.scrollUp.y, L.scrollUp.r, true);
    }
    if (L.scrollDown) {
      drawTriangle(ctx, L.scrollDown.x, L.scrollDown.y, L.scrollDown.r, false);
    }

    ctx.restore();
  }

  // ── Layout ────────────────────────────────────────────────────────────────

  private _computeLayout(tabs: DebugTab[], viewport: { w: number; h: number }): FrameLayout {
    const activeTab = tabs.find(t => t.id === this._activeTab) ?? tabs[0];
    const hasSubtabs = !!activeTab?.subtabs && activeTab.subtabs.length > 0;
    const subtabRowH = hasSubtabs ? SUBTAB_H : 0;
    const activeSubtab = hasSubtabs
      ? (activeTab!.subtabs!.find(s => s.id === this._activeSubtab) ?? activeTab!.subtabs![0])
      : null;
    const allRows: DebugAction[] = activeSubtab
      ? activeSubtab.actions
      : (activeTab?.actions ?? []);

    // Clamp scroll
    const maxScroll = Math.max(0, allRows.length - MAX_ROWS);
    if (this._scrollOffset > maxScroll) this._scrollOffset = maxScroll;
    const visibleRows = allRows.slice(this._scrollOffset, this._scrollOffset + MAX_ROWS);

    const contentH = visibleRows.length * ROW_H;
    const h = TITLE_H + TAB_H + subtabRowH + contentH + PANEL_PAD_Y * 2 + 4;

    // Anchor → top-left, clamped to viewport
    let x = (this._anchor?.x ?? 100) - PANEL_W / 2;
    let y = (this._anchor?.y ?? 100) - h / 2;
    if (x < 8) x = 8;
    if (y < 8) y = 8;
    if (x + PANEL_W > viewport.w - 8) x = Math.max(8, viewport.w - 8 - PANEL_W);
    if (y + h > viewport.h - 8) y = Math.max(8, viewport.h - 8 - h);

    const bg = { x, y, w: PANEL_W, h };
    const closeBtn = { x: x + PANEL_W - 12, y: y + 11, r: CLOSE_R };
    const dragBtn  = { x: x + 12,            y: y + 11, r: DRAG_R };

    // Tabs row
    const tabRects: FrameLayout['tabRects'] = [];
    const tabRowY = y + TITLE_H;
    const tabAreaX = x + 8;
    const tabAreaW = PANEL_W - 16;
    const tabW = tabs.length > 0 ? tabAreaW / tabs.length : 0;
    for (let i = 0; i < tabs.length; i++) {
      tabRects.push({ x: tabAreaX + i * tabW, y: tabRowY, w: tabW, h: TAB_H });
    }

    // Subtabs row
    const subtabRects: FrameLayout['subtabRects'] = [];
    if (hasSubtabs && activeTab) {
      const subs = activeTab.subtabs!;
      const subRowY = tabRowY + TAB_H;
      const subAreaX = x + 8;
      const subAreaW = PANEL_W - 16;
      const subW = subs.length > 0 ? subAreaW / subs.length : 0;
      for (let i = 0; i < subs.length; i++) {
        subtabRects.push({ x: subAreaX + i * subW, y: subRowY, w: subW, h: SUBTAB_H });
      }
    }

    // Rows
    const rowRects: FrameLayout['rowRects'] = [];
    const rowStartY = y + TITLE_H + TAB_H + subtabRowH + 4;
    for (let i = 0; i < visibleRows.length; i++) {
      rowRects.push({
        x: x + PANEL_PAD_X,
        y: rowStartY + i * ROW_H,
        w: PANEL_W - PANEL_PAD_X * 2,
        h: ROW_H - 2,
      });
    }

    // Scroll arrows (only when there's overflow)
    let scrollUp: FrameLayout['scrollUp'];
    let scrollDown: FrameLayout['scrollDown'];
    if (allRows.length > MAX_ROWS) {
      const arrowX = x + PANEL_W - 14;
      if (this._scrollOffset > 0) {
        scrollUp = { x: arrowX, y: rowStartY + 8, r: 7 };
      }
      if (this._scrollOffset < maxScroll) {
        scrollDown = { x: arrowX, y: rowStartY + contentH - 8, r: 7 };
      }
    }

    return {
      bg, closeBtn, dragBtn,
      tabRects, subtabRects, rowRects,
      scrollUp, scrollDown,
      firstRowIdx: this._scrollOffset,
      visibleRows,
    };
  }
}

// ── Small helpers ───────────────────────────────────────────────────────────

function dist(x1: number, y1: number, x2: number, y2: number): number {
  const dx = x1 - x2, dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.arcTo(x + w, y, x + w, y + rr, rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.arcTo(x + w, y + h, x + w - rr, y + h, rr);
  ctx.lineTo(x + rr, y + h);
  ctx.arcTo(x, y + h, x, y + h - rr, rr);
  ctx.lineTo(x, y + rr);
  ctx.arcTo(x, y, x + rr, y, rr);
  ctx.closePath();
}

function drawTriangle(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, r: number, pointUp: boolean,
): void {
  ctx.save();
  ctx.fillStyle = 'rgba(58, 32, 16, 0.55)';
  ctx.beginPath();
  if (pointUp) {
    ctx.moveTo(cx, cy - r * 0.6);
    ctx.lineTo(cx - r * 0.6, cy + r * 0.4);
    ctx.lineTo(cx + r * 0.6, cy + r * 0.4);
  } else {
    ctx.moveTo(cx, cy + r * 0.6);
    ctx.lineTo(cx - r * 0.6, cy - r * 0.4);
    ctx.lineTo(cx + r * 0.6, cy - r * 0.4);
  }
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}
